'use client'
import { useState, useEffect, useCallback } from 'react'
import { BREAKPOINTS, DEVICE_TYPES } from '@/constants'

type BreakpointKey = keyof typeof BREAKPOINTS

interface IWindowDimensions {
  width: number
  height: number
  isXs: boolean
  isMobile: boolean
  isTablet: boolean
  isDesktop: boolean
  isLargeDesktop: boolean
  isExtraLargeDesktop: boolean
  isLtTablet: boolean
  isLtDesktop: boolean
  isLtLargeDesktop: boolean
  isGtMobile: boolean
  isGtTablet: boolean
  isGtDesktop: boolean
  isGtLargeDesktop: boolean
  screen: string | null
}

/**
 * Custom hook to get window dimensions and device type
 * @returns Window dimensions and device type information
 */
export function useWindowDimensions(): IWindowDimensions {
  if (typeof window === 'undefined') {
    return {
      width: 0,
      height: 0,
      isXs: false,
      isMobile: false,
      isTablet: false,
      isDesktop: false,
      isLargeDesktop: false,
      isExtraLargeDesktop: false,
      isLtTablet: false,
      isLtDesktop: false,
      isLtLargeDesktop: false,
      isGtMobile: false,
      isGtTablet: false,
      isGtDesktop: false,
      isGtLargeDesktop: false,
      screen: null,
    }
  }

  const width = window.innerWidth || 0
  const height = window.innerHeight || 0

  const screenType = (() => {
    if (width < BREAKPOINTS.XSMALL) return DEVICE_TYPES.XSMALL
    if (width < BREAKPOINTS.MOBILE) return DEVICE_TYPES.MOBILE
    if (width < BREAKPOINTS.TABLET) return DEVICE_TYPES.TABLET
    if (width < BREAKPOINTS.DESKTOP) return DEVICE_TYPES.DESKTOP
    if (width < BREAKPOINTS.EXTRA_LARGE_DESKTOP) return DEVICE_TYPES.LARGE_DESKTOP
    return DEVICE_TYPES.EXTRA_LARGE_DESKTOP
  })()

  return {
    width,
    height,
    isXs: width < BREAKPOINTS.MOBILE,
    isMobile: width < BREAKPOINTS.MOBILE,
    isTablet: width >= BREAKPOINTS.MOBILE && width < BREAKPOINTS.TABLET,
    isDesktop: width >= BREAKPOINTS.TABLET,
    isLargeDesktop: width >= BREAKPOINTS.DESKTOP,
    isExtraLargeDesktop: width >= BREAKPOINTS.EXTRA_LARGE_DESKTOP,
    isLtTablet: width < BREAKPOINTS.TABLET,
    isLtDesktop: width < BREAKPOINTS.DESKTOP,
    isLtLargeDesktop: width < BREAKPOINTS.EXTRA_LARGE_DESKTOP,
    isGtMobile: width > BREAKPOINTS.MOBILE,
    isGtTablet: width > BREAKPOINTS.TABLET,
    isGtDesktop: width > BREAKPOINTS.DESKTOP,
    isGtLargeDesktop: width > BREAKPOINTS.EXTRA_LARGE_DESKTOP,
    screen: screenType,
  }
}